import {
  Button,
  ButtonGroup,
  Grid,
  GridItem,
  Heading,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
} from "@chakra-ui/react";
import { McpSettings } from "@components/modules/users/listing/form/user-modal-tabs/mcp-settings";
import { LoadingOverlay } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { CalendarIntegrations } from "@src/components/modules/users/listing/form/user-modal-tabs/calendar-integrations";
import { HourlyRates } from "@src/components/modules/users/listing/form/user-modal-tabs/HourlyRates";
import { NotificationSettings } from "@src/components/modules/users/listing/form/user-modal-tabs/NotificationSettings";
import { PersonalSettings } from "@src/components/modules/users/listing/form/user-modal-tabs/PersonalSettings";
import { PlanningUtilization } from "@src/components/modules/users/listing/form/user-modal-tabs/PlanningUtilization";
import { UserStatus } from "@src/components/modules/users/listing/form/user-modal-tabs/UserStatus";
import type { TCategory } from "@src/stores/EditUserModalStore";
import type { UserType } from "@src/stores/models/Me";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { AccessSettings } from "./user-modal-tabs/AccessSettings";

const MENU_COMPONENT_MAP = {
  personal: <PersonalSettings />,
  notifications: <NotificationSettings />,
  "planning-utilization": <PlanningUtilization />,
  "user-status": <UserStatus />,
  "hourly-rates": <HourlyRates />,
  access: <AccessSettings />,
  "calendar-integrations": <CalendarIntegrations />,
  mcp: <McpSettings />,
} as const;

type MenuItem = {
  key: TCategory;
  label: string;
  accessByUserType: UserType[];
  accessByPermissions: boolean;
};

export const EditUserModal = observer(function EditUserModal() {
  const { editUserModalStore, authStore, workspaceStore } = useStore();
  const MENU_ITEMS: MenuItem[] = useMemo(() => {
    return (
      [
        {
          key: "personal",
          label: t`Personal settings`,
          accessByUserType: ["internal", "client", "partner"],
          accessByPermissions: true,
        },
        {
          key: "notifications",
          label: t`Notifications`,
          accessByUserType: ["internal", "client", "partner"],
          accessByPermissions: true,
        },
        {
          key: "planning-utilization",
          label: t`Planning & Utilization`,
          accessByUserType: ["internal"],
          accessByPermissions: true,
        },
        {
          key: "user-status",
          label: t`User status`,
          accessByUserType: ["internal", "client", "partner"],
          accessByPermissions:
            editUserModalStore.modalState.additionalData?.userType ===
            "internal"
              ? can("user_updateAllfredData")
              : can("user_updateAllfredData", "user_updateScopeUser"),
        },
        {
          key: "hourly-rates",
          label: t`Internal hourly rates`,
          accessByUserType: ["internal"],
          accessByPermissions: can("user_accessAccountingAttributes"),
        },
        {
          key: "access",
          label: t`Access`,
          accessByUserType: ["client"],
          accessByPermissions: can(
            "user_updateAllfredData",
            "user_updateScopeUser",
          ),
        },
        {
          key: "calendar-integrations",
          label: t`Calendar integrations`,
          accessByUserType: ["internal"],
          accessByPermissions:
            authStore.user?.id ===
              editUserModalStore.modalState.additionalData?.userId &&
            !!workspaceStore.settings?.enableCalendarIntegration,
        },
        {
          key: "mcp",
          label: t`MCP integration`,
          accessByUserType: ["internal"],
          accessByPermissions:
            authStore.user?.id ===
              editUserModalStore.modalState.additionalData?.userId &&
            !!workspaceStore.settings?.showMcpSettings,
        },
      ] as MenuItem[]
    ).filter((item) => {
      const accessByUserType = item.accessByUserType.includes(
        editUserModalStore.modalState.additionalData?.userType!,
      );

      return accessByUserType && item.accessByPermissions;
    });
  }, [
    authStore.user?.id,
    editUserModalStore.modalState.additionalData?.userType,
    editUserModalStore.modalState.additionalData?.userId,
    workspaceStore.settings?.showMcpSettings,
  ]);

  return (
    <Modal
      isCentered
      isOpen={editUserModalStore.modalState.isOpen}
      onClose={editUserModalStore.modalState.onClose}
      size={{
        base: "full",
        md: "6xl",
      }}
      trapFocus={false}
    >
      <ModalOverlay />

      <ModalContent>
        <LoadingOverlay
          isLoading={editUserModalStore.isLoading}
          position="absolute"
          rounded="xl"
          overflow="hidden"
          transparent={false}
        >
          <Grid templateRows="60vh auto" templateColumns="25% 1fr">
            <GridItem
              borderInlineEndColor="grey.200"
              borderInlineEndStyle="solid"
              borderInlineEndWidth="1px"
              rowSpan={2}
            >
              <ModalHeader as="h2">
                <Trans>Edit user</Trans>
              </ModalHeader>
              <ButtonGroup
                flexDir="column"
                w="full"
                mt="1"
                orientation="vertical"
                paddingBlock="1"
                paddingInline="3"
                variant="ghost"
              >
                {MENU_ITEMS.map((item) => (
                  <Button
                    key={item.key}
                    justifyContent="start"
                    isActive={editUserModalStore.category === item.key}
                    onClick={() => editUserModalStore.setCategory(item.key)}
                  >
                    {item.label}
                  </Button>
                ))}
              </ButtonGroup>
            </GridItem>
            <GridItem pos="relative" overflowY="auto">
              <Stack justify="center" h={63} px="6">
                <Heading size="md">
                  {
                    MENU_ITEMS.find(
                      (item) => item.key === editUserModalStore.category,
                    )?.label
                  }
                </Heading>
                <ModalCloseButton bg="white" />
              </Stack>
              <Stack p="6">
                {MENU_COMPONENT_MAP[editUserModalStore.category]}
              </Stack>
            </GridItem>
            <GridItem
              textAlign="end"
              borderBlockStartColor="grey.200"
              borderBlockStartStyle="solid"
              borderBlockStartWidth="1px"
              paddingBlock="2"
              paddingInline="6"
            >
              <Button
                colorScheme="violet"
                isLoading={editUserModalStore.isSubmitting}
                onClick={editUserModalStore.onSubmit}
              >
                <Trans>Update</Trans>
              </Button>
            </GridItem>
          </Grid>
        </LoadingOverlay>
      </ModalContent>
    </Modal>
  );
});
